"use client";

import { mergeClasses } from "@trex-2b/libs";
import { useEffect, useState } from "react";
import type { CSSProperties, PropsWithChildren } from "react";
import { CommonIcon } from "./common-icon/common-icon";
import "./modal.css";

const MODAL_ANIMATION_DURATION_MS = 300;

export type ModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  overlayClassName?: string;
  testId: string;
}>;

export function Modal({
  isOpen,
  onClose,
  children,
  className,
  overlayClassName,
  testId,
}: ModalProps) {
  const [shouldMount, setShouldMount] = useState(isOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldMount(true);
      const frameId = requestAnimationFrame(() => {
        setIsVisible(true);
        setHasBeenVisible(true);
      });
      return () => {
        cancelAnimationFrame(frameId);
      };
    }

    setIsVisible(false);
    const unmountTimer = window.setTimeout(() => {
      setShouldMount(false);
      setHasBeenVisible(false);
    }, MODAL_ANIMATION_DURATION_MS);

    return () => {
      window.clearTimeout(unmountTimer);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!shouldMount) {
    return null;
  }

  const animationStyle = {
    "--trex2b-modal-animation-duration": `${MODAL_ANIMATION_DURATION_MS}ms`,
  } as CSSProperties;

  return (
    <div
      role="presentation"
      className={mergeClasses(
        "fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[20px]",
        overlayClassName,
      )}
      style={{
        ...animationStyle,
        opacity: isVisible ? 1 : 0,
        transition: `opacity ${MODAL_ANIMATION_DURATION_MS}ms ease-in-out`,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        data-testid={testId}
        className={mergeClasses(
          "relative min-h-[548px] w-[461px] max-h-[calc(100vh-32px)] max-w-[calc(100vw-32px)] rounded-xl bg-white p-5 shadow-[0_12px_40px_rgba(0,0,0,0.2)]",
          isVisible
            ? "trex2b-modal-fade-in"
            : hasBeenVisible
              ? "trex2b-modal-fade-out"
              : "",
          className,
        )}
        style={animationStyle}
      >
        <button
          type="button"
          aria-label="Close modal"
          data-testid={`${testId}-close-btn`}
          className="absolute right-4 top-4 inline-flex h-7 w-7 items-center justify-center rounded-md text-[#1c1916] transition-colors hover:bg-black/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-400/60 cursor-pointer"
          onClick={onClose}
        >
          <CommonIcon iconName="Close" className="size-[15px]" />
        </button>
        {children}
      </div>
    </div>
  );
}
